import { useCallback } from "react";
import { useSelector } from "react-redux";
import {
  isModuleEnabled as isModuleEnabledUtil,
  isPermissionVisible as isPermissionVisibleUtil,
} from "../utils/moduleVisibility";

/**
 * Hook to access user permissions and check permission access.
 * Permissions are fetched on login/checkAuth and stored in Redux.
 * Module visibility: enabledModules from GET /permission/me (RBAC) gates UI before permission slugs.
 */
export const usePermissions = () => {
  const { permissions, enabledModules, loading } = useSelector((state) => state.permissions);

  const hasPermission = useCallback(
    (slug) => {
      if (!slug || !Array.isArray(permissions)) return false;
      return permissions.includes(slug);
    },
    [permissions]
  );

  const isModuleEnabled = useCallback(
    (slugOrModuleKey) => isModuleEnabledUtil(enabledModules, slugOrModuleKey),
    [enabledModules]
  );

  /** UI/routes: RBAC module enabled AND permission slug present. */
  const hasVisiblePermission = useCallback(
    (slug) => isPermissionVisibleUtil(enabledModules, permissions, slug),
    [enabledModules, permissions]
  );

  const hasAnyPermission = useCallback(
    (slugs) => {
      if (!Array.isArray(slugs) || slugs.length === 0 || !Array.isArray(permissions))
        return false;
      return slugs.some((s) => hasVisiblePermission(s));
    },
    [hasVisiblePermission]
  );

  const hasAllPermissions = useCallback(
    (slugs) => {
      if (!Array.isArray(slugs) || slugs.length === 0 || !Array.isArray(permissions))
        return false;
      return slugs.every((s) => hasVisiblePermission(s));
    },
    [hasVisiblePermission]
  );

  const hasAnyPermissionRaw = useCallback(
    (slugs) => {
      if (!Array.isArray(slugs) || slugs.length === 0 || !Array.isArray(permissions))
        return false;
      return slugs.some((s) => permissions.includes(s));
    },
    [permissions]
  );

  return {
    permissions: permissions || [],
    enabledModules,
    hasPermission,
    isModuleEnabled,
    hasVisiblePermission,
    hasAnyPermission,
    hasAllPermissions,
    hasAnyPermissionRaw,
    isLoading: loading,
  };
};
